import {Constants} from '../constants/constants';
import {StringOrStringArrayObject} from '../../models/object.model';

export class UrlFunctions {

  /**
   * Method: Get current url
   */
  public static getCurrentUrl(): string {
    return window.location.href;
  }

  /**
   * Method: Get current url without get params
   */
  public static getCurrentUrlWithoutGetParams(): string {
    return this.getCurrentUrl().split(Constants.QUESTION_MARK)[0];
  }

  /**
   * Method: Check router url by current app url
   * @param routeUrl - This user url
   */
  public static isPageByRouteUrl(routeUrl: string): boolean {
    return !!this.getCurrentUrlWithoutGetParams().includes(routeUrl);
  }

  /**
   * Method: Get URL without query parameters
   * @param url - The URL to strip query parameters from
   * @returns The URL without query parameters
   * @description
   * This method takes a URL and returns the same URL without any query parameters.
   */
  public static getUrlWithoutQueryParams(url: string): string {
    return url.split(Constants.QUESTION_MARK)[0];
  }

  /**
   * Method: Get query params from a given URL
   * @param url - The URL to extract query parameters from
   * @returns An object with key-value pairs of query parameters
   * @description
   * This method extracts query parameters from a given URL and returns them as an object.
   * The object will have the query parameter keys as property names and the values as the property values.
   * If no query parameters are found, an empty object is returned.
   */
  public static getQueryParamsFromUrl(url: string): StringOrStringArrayObject {
    const queryParams: StringOrStringArrayObject = {};

    // Check if the URL has query parameters
    const queryStringIndex = url.indexOf(Constants.QUESTION_MARK);
    if (queryStringIndex === -1) {
      return queryParams; // No query parameters found
    }

    // Extract the query string
    const queryString = url.slice(queryStringIndex + 1);

    // Split the query string into key-value pairs
    const pairs = queryString.split(Constants.AMPERSAND);

    // Process each pair
    pairs.forEach(pair => {
      const [key, value] = pair.split(Constants.EQUALS);

      // Decode URI components to handle special characters
      const decodedKey = decodeURIComponent(key);
      const decodedValue = decodeURIComponent(value);

      // Handle repeated parameters
      if (Array.isArray(queryParams[decodedKey])) {
        queryParams[decodedKey].push(decodedValue);
      } else if (queryParams[decodedKey]) {
        queryParams[decodedKey] = [queryParams[decodedKey], decodedValue];
      } else {
        queryParams[decodedKey] = decodedValue;
      }
    });

    return queryParams;
  }

  /**
   * Method: Check if a URL has query parameters
   * @param url - The URL to check
   * @returns True if the URL has query parameters, false otherwise
   */
  public static hasQueryParams(url: string): boolean {
    return url.includes(Constants.QUESTION_MARK);
  }

  /**
   * Method: Build an `mailto:` URL for the given email address.
   * Centralizes the construction so future enrichment (subject, cc, body) lives in one place.
   * @param email - The email address to construct the mailto URL for
   * @returns A mailto URL in the form `mailto:<email>`
   */
  public static buildMailto(email: string): string {
    return `mailto:${email}`;
  }

}
